import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { isIOS } from 'react-device-detect';
import SEOHead from './SEOHead';
import StarryBackground from './StarryBackground';
import { useI18n } from '../i18n';
import { getChatThumbnail } from '../utils/thumbnailGenerator';

const ChatRoomPage = ({ primaryAction, secondaryAction, isAppInstalled }) => {
  const { roomId } = useParams();
  const { t } = useI18n();
  const [chatData, setChatData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChatData = async () => {
      try {
        // Chiamata API per metadati chatroom (per SEO e preview)
        // Usa la stessa API Lambda che hai per i dettagli chat
        const response = await fetch(`https://your-api.com/chat-metadata/${roomId}`);
        
        if (response.ok) {
          const data = await response.json();
          setChatData(data);
        } else {
          // Fallback: dati generici
          setChatData({
            name: 'Chat di gruppo',
            destination: 'Destinazione sconosciuta',
            memberCount: 'N/A',
            creator: 'Utente WanderBuddy'
          });
        }
      } catch (err) {
        console.log('Error fetching chat metadata:', err);
        // Fallback silenzioso
        setChatData({
          name: 'Chat di gruppo WanderBuddy',
          destination: 'Viaggio',
          memberCount: 'Vari membri',
          creator: 'Utente WanderBuddy'
        });
      } finally {
        setLoading(false);
      }
    };

    if (roomId) {
      fetchChatData();
    } else {
      setLoading(false);
    }
  }, [roomId]);

  const pageTitle = `Chat: ${chatData?.name || 'Gruppo di viaggio'} | WanderBuddy`;
  const pageDescription = t('chat.private_desc');

  if (loading) {
    return (
      <>
        <StarryBackground />
        <div className="sora-layout">
          <motion.header 
            className="sora-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="sora-header-content">
              <div className="sora-logo-text">
                <img src="/wanderbuddy-white.png" alt="WanderBuddy" style={{ height: '32px', width: 'auto' }} />
              </div>
              <div className="sora-header-actions">
                <motion.a
                  href="https://apps.apple.com/app/id6737308693"
                  className="sora-btn sora-btn-download"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Download
                </motion.a>
              </div>
            </div>
          </motion.header>
          <motion.main 
            className="sora-main"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <div className="sora-loading">
              <div className="spinner"></div>
              <p>Caricamento chat...</p>
            </div>
          </motion.main>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        type="website"
        image={getChatThumbnail(chatData)}
      />
      
      <StarryBackground />
      
      <div className="sora-layout">
        {/* Header capsule */}
        <motion.header 
          className="sora-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="sora-header-content">
            <div className="sora-logo-text">
              <img src="/wanderbuddy-white.png" alt="WanderBuddy" style={{ height: '32px', width: 'auto' }} />
            </div>
            <div className="sora-header-actions">
              <motion.a
                href="https://apps.apple.com/app/id6737308693"
                className="sora-btn sora-btn-download"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Download
              </motion.a>
              <motion.button
                className="sora-btn sora-btn-login"
                disabled
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
              >
                Login
              </motion.button>
            </div>
          </div>
        </motion.header>

        {/* Main content */}
        <motion.main 
          className="sora-main"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          {/* Chat Hero Card */}
          <motion.div
            className="sora-hero-card"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <div className="sora-card-header">
              <div className="sora-icon-badge" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                🗣️
              </div>
              <div className="sora-card-labels">
                <span className="sora-card-label">{t('chat.group_chat')}</span>
                <span className="sora-card-sublabel">WanderBuddy</span>
              </div>
            </div>

            <h1 className="sora-hero-title">
              {chatData?.name || t('chat.group_chat')}
            </h1>

            <div className="sora-chat-meta">
              <div className="sora-meta-item">
                <span className="sora-meta-icon">📍</span>
                <span className="sora-meta-text">{chatData?.destination || '—'}</span>
              </div>
              <div className="sora-meta-item">
                <span className="sora-meta-icon">👥</span>
                <span className="sora-meta-text">{chatData?.memberCount}</span>
              </div>
              <div className="sora-meta-item">
                <span className="sora-meta-icon">✨</span>
                <span className="sora-meta-text">{t('chat.created_by', { creator: chatData?.creator || 'Utente' })}</span>
              </div>
            </div>

            <p className="sora-hero-description">
              {t('chat.private_desc')}
            </p>

            {/* Action Buttons */}
            <div className="sora-actions">
              {primaryAction && (
                <motion.a
                  href={primaryAction.href}
                  className={`sora-action-btn ${primaryAction.primary ? 'sora-action-primary' : 'sora-action-secondary'}`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="sora-btn-icon">💬</span>
                  <span className="sora-btn-text">
                    {isIOS ? 'Apri nell\'App' : 'Scarica WanderBuddy'}
                  </span>
                </motion.a>
              )}
              
              {secondaryAction && (
                <motion.a
                  href={secondaryAction.href}
                  className="sora-action-btn sora-action-secondary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="sora-btn-text">{secondaryAction.text}</span>
                </motion.a>
              )}
            </div>

            {/* Features List */}
            <div className="sora-features-list">
              <div className="sora-feature-item">
                <span className="sora-feature-icon">💬</span>
                <div className="sora-feature-content">
                  <h3 className="sora-feature-title">Messaggistica in tempo reale</h3>
                  <p className="sora-feature-desc">Chatta con gli altri membri del gruppo</p>
                </div>
              </div>
              
              <div className="sora-feature-item">
                <span className="sora-feature-icon">📍</span>
                <div className="sora-feature-content">
                  <h3 className="sora-feature-title">Pianificazione collaborativa</h3>
                  <p className="sora-feature-desc">Organizza attività e condividi idee</p>
                </div>
              </div>
              
              <div className="sora-feature-item">
                <span className="sora-feature-icon">🔔</span>
                <div className="sora-feature-content">
                  <h3 className="sora-feature-title">Notifiche push</h3>
                  <p className="sora-feature-desc">Resta aggiornato su ogni messaggio</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Info Box */}
          <motion.div
            className="sora-info-box"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <div className="sora-info-icon">ℹ️</div>
            <div className="sora-info-content">
              <h3 className="sora-info-title">{t('chat.private_title')}</h3>
              <p className="sora-info-text">
                Questa chat è privata. Per visualizzarla e partecipare alla conversazione, 
                devi essere invitato da un membro del gruppo. Apri l'app per verificare il tuo accesso.
              </p>
            </div>
          </motion.div>
        </motion.main>

        {/* Footer */}
        <motion.footer
          className="sora-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <div className="sora-footer-content">
            <div className="sora-footer-logo">
              <img src="/wanderbuddy-white.png" alt="WanderBuddy" style={{ height: '24px', width: 'auto', opacity: 0.6 }} />
            </div>
            <p className="sora-footer-text">{t('common.footer')}</p>
          </div>
        </motion.footer>
      </div>
    </>
  );
};

export default ChatRoomPage;

